import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Tooltip } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import { RoundInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { ExcelTransactionAccountType } from 'store/app/enums';
import { getExcelTransactionAccountTypeLabel } from 'utils/display-enum-label';
import {
  fetchChartOfAccountsByCountryRequest,
  saveChartOfAccountsRequest,
} from 'store/chart-of-accounts/actions';
import { CoaAccountRow, CoaTemplate } from 'store/chart-of-accounts/models';
import {
  getChartOfAccountTemplates,
  getChartOfAccountsByCountry,
} from 'store/chart-of-accounts/selectors';
import { getCommonCountries } from 'store/common/selectors';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { getReturnTypesData } from 'store/returns/selectors';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import styled, { createGlobalStyle } from 'styled-components';

const Wrapper = styled.div<{ pwaMobile?: boolean }>`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: ${({ pwaMobile }) => (pwaMobile ? 'flex-start' : 'space-between')};
  height: ${({ pwaMobile }) =>
    pwaMobile ? '100%' : 'calc(95vh - 110px)'};
  min-height: ${({ pwaMobile }) => (pwaMobile ? '0' : 'auto')};
  flex: ${({ pwaMobile }) => (pwaMobile ? '1 1 auto' : '0 1 auto')};
  .divider-or {
    display: flex;
    align-items: center;
    margin: 8px 0 16px 0;
    color: var(--color-text-muted, #5a6a6a);
    font-size: 15px;
    font-weight: 500;
  }
  .divider-or:before,
  .divider-or:after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--color-border, #dbeae3);
    margin: 0 12px;
  }
  .coa-section {
    background-color: var(--color-surface, #f8fbfb);
    border-left: 3px solid var(--color-primary, #41aaaf);
    margin: 8px 0;
    padding: 12px 16px;
    border-radius: 0 4px 4px 0;
    min-height: 60px;
  }
`;

const AccountListWrapper = styled.div<{ deduct: number; pwaMobile?: boolean }>`
  max-height: ${({ deduct, pwaMobile }) =>
    pwaMobile ? 'none' : `calc(95vh - ${deduct}px)`};
  min-height: 45px;
  overflow-y: ${({ pwaMobile }) => (pwaMobile ? 'visible' : 'auto')};
  background: var(--color-surface-raised, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 0 8px 12px 8px;
  margin-bottom: 15px;
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const AccountItem = styled.label`
  display: flex;
  align-items: center;
  font-size: 12px;
  min-height: 18px;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AccountText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountTypeLabel = styled.span`
  font-family: Inter;
  font-size: 11px;
  color: var(--color-text-muted, #5a6a6a);
  flex-shrink: 0;
  margin-left: 6px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionHeader = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text-dark, #042a2a);
  margin-top: 12px;
  margin-bottom: 8px;
  &:first-child {
    margin-top: 0;
  }
`;

const MasterCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  background: var(--color-surface-raised, #fff);
  z-index: 2;
  padding-top: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--color-border, #dbeae3);
  box-shadow: 0 2px 0 0 #fff;
`;

const CustomDrawerBodyStyle = createGlobalStyle`
  .ant-drawer-body:has(.add-chart-of-accounts-form-wrapper) {
    padding-bottom: 0 !important;
  }
`;

const CustomNoticeLinkStyle = createGlobalStyle`
  .custom-notice-link-coa {
    color: var(--color-primary, #41aaaf);
    font-size: 13px;
    font-family: Inter, sans-serif;
    font-weight: 700;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration 0.2s;
  }
  .custom-notice-link-coa:hover, .custom-notice-link-coa:focus {
    text-decoration: underline !important;
    color: var(--color-primary, #41aaaf) !important;
  }
`;

const labelStyle = { marginBottom: 9 };
const dropdownStyle = { marginBottom: 12 };
const countryDropdownStyle = { marginBottom: 32 };

interface AddChartOfAccountsFormProps {
  organisationId?: string;
  loading?: boolean;
  onSuccess?: () => void;
  /** When provided, show a "Skip for now" button next to Add Account that calls this and closes the drawer (caller should close). */
  onSkipCoa?: () => void;
  /** When set, the country dropdown is pre-selected to this country (e.g. entity's country). */
  initialCountryId?: string;
}

const AccountEllipsisWithTooltip: React.FC<{
  children: React.ReactNode;
  title: string;
}> = ({ children, title }) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children, title]);

  const content = <AccountText ref={textRef}>{children}</AccountText>;
  return isTruncated ? (
    <Tooltip title={title} placement="left">
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export const AddChartOfAccountsForm: React.FC<AddChartOfAccountsFormProps> = ({
  organisationId,
  loading = false,
  onSuccess,
  onSkipCoa,
  initialCountryId,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const pwaMobileNav = usePwaMobileNavEnabled();
  const dispatch = useDispatch();
  const countriesRaw = useSelector(getCommonCountries);
  const returnTypesData = useSelector(getReturnTypesData);
  const templates = (useSelector(getChartOfAccountTemplates) ||
    []) as CoaTemplate[];
  const selectedCountryAccounts = useSelector(getChartOfAccountsByCountry);
  const countries = Array.isArray(countriesRaw) ? countriesRaw : [];

  const [selectedTemplateId, setSelectedTemplateId] = React.useState<
    string | undefined
  >(undefined);
  const [selectedCountryId, setSelectedCountryId] = React.useState<
    string | undefined
  >(initialCountryId ?? undefined);
  const [selectedAccountIds, setSelectedAccountIds] = React.useState<string[]>(
    []
  );
  const [hasReadNotice, setHasReadNotice] = React.useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = React.useState('');

  const organisations = useSelector(getOrgOrganisations) || [];

  const { existingCoaAccountIds, existingCoaAccountKeys } =
    React.useMemo(() => {
      const empty = {
        existingCoaAccountIds: new Set<string>(),
        existingCoaAccountKeys: new Set<string>(),
      };
      if (!organisationId || !Array.isArray(organisations)) return empty;

      const org = organisations.find((o) => o.uuid === organisationId);
      const existing = (org?.chartOfAccounts ?? []) as CoaAccountRow[];

      const ids = new Set<string>();
      const keys = new Set<string>();

      existing.forEach((a) => {
        if (a.id) ids.add(a.id);
        const key = `${(a.name || '').trim()}::${a.type || ''}`;
        keys.add(key);
      });

      return {
        existingCoaAccountIds: ids,
        existingCoaAccountKeys: keys,
      };
    }, [organisationId, organisations]);

  const filteredCountries = React.useMemo(() => {
    const returnTypesEdit = returnTypesData?.returnTypesEdit;
    if (!Array.isArray(returnTypesEdit) || returnTypesEdit.length === 0) {
      return countries.filter((c) => c && (c as any).scopeSupported);
    }
    const availableCountryIds = new Set(
      returnTypesEdit
        .map((returnType: any) => returnType.countryId)
        .filter(Boolean)
    );
    return countries.filter(
      (c) =>
        c && (c as any).scopeSupported && availableCountryIds.has((c as any).id)
    );
  }, [countries, returnTypesData?.returnTypesEdit]);

  const accountsToShow: CoaAccountRow[] = React.useMemo(() => {
    // If a template is selected, use its accounts
    if (selectedTemplateId) {
      const tpl = templates.find((t) => t.id === selectedTemplateId);
      if (!tpl || !Array.isArray(tpl.accounts)) return [];
      return tpl.accounts as CoaAccountRow[];
    }

    // Otherwise, fall back to country-based accounts
    if (!selectedCountryId) return [];
    if (
      !selectedCountryAccounts ||
      !selectedCountryAccounts.accounts ||
      selectedCountryAccounts.countryId !== selectedCountryId
    ) {
      return [];
    }

    return (selectedCountryAccounts.accounts as any[]).map((raw, index) => {
      const id = (raw.id as string) ?? `${index}`;
      const accountCode = (raw.accountCode as string) ?? '';
      const accountName =
        (raw.accountName as string) ?? (raw.name as string) ?? '';
      const accountType =
        (raw.accountType as string) ?? (raw.type as string) ?? '';
      const recommended =
        typeof raw.recommended === 'boolean' ? raw.recommended : false;

      const name =
        accountCode && accountName
          ? `${accountCode} - ${accountName}`
          : accountName || accountCode || '-';

      return {
        id,
        name,
        type: accountType as unknown as ExcelTransactionAccountType,
        recommended,
        custom: false,
      } as CoaAccountRow;
    });
  }, [
    selectedTemplateId,
    templates,
    selectedCountryId,
    selectedCountryAccounts,
  ]);

  React.useEffect(() => {
    if (selectedCountryId) {
      dispatch(fetchChartOfAccountsByCountryRequest(selectedCountryId));
    }
  }, [selectedCountryId, dispatch]);
  const selectableAccounts = React.useMemo(
    () =>
      accountsToShow.filter((a) => {
        const key = `${(a.name || '').trim()}::${a.type || ''}`;
        return (
          !existingCoaAccountIds.has(a.id) && !existingCoaAccountKeys.has(key)
        );
      }),
    [accountsToShow, existingCoaAccountIds, existingCoaAccountKeys]
  );

  const recommendedAccounts = accountsToShow.filter((a) =>
    Boolean(a.recommended)
  );
  const otherAccounts = accountsToShow.filter((a) => !a.recommended);

  const accountMatchesSearch = React.useCallback(
    (account: CoaAccountRow) => {
      if (!accountSearchTerm.trim()) return true;
      const term = accountSearchTerm.trim().toLowerCase();
      const name = (account.name ?? '').toLowerCase();
      const typeLabel = getExcelTransactionAccountTypeLabel(account.type ?? '', t).toLowerCase();
      return name.includes(term) || typeLabel.includes(term);
    },
    [accountSearchTerm]
  );

  const filteredRecommendedAccounts =
    recommendedAccounts.filter(accountMatchesSearch);
  const filteredOtherAccounts = otherAccounts.filter(accountMatchesSearch);
  const filteredAllAccounts = accountsToShow.filter(accountMatchesSearch);

  const filteredSelectableAccounts = React.useMemo(
    () => selectableAccounts.filter((a) => accountMatchesSearch(a)),
    [selectableAccounts, accountMatchesSearch]
  );

  const selectableRecommendedAccountIds = React.useMemo(
    () => selectableAccounts.filter((a) => a.recommended).map((a) => a.id),
    [selectableAccounts]
  );

  React.useEffect(() => {
    if (selectableRecommendedAccountIds.length > 0) {
      setSelectedAccountIds((prev) => {
        const merged = new Set([...prev, ...selectableRecommendedAccountIds]);
        return Array.from(merged);
      });
    }
  }, [
    selectableRecommendedAccountIds.length > 0
      ? selectableRecommendedAccountIds.join(',')
      : '',
  ]);

  const visibleSelectableCount = filteredSelectableAccounts.length;
  const visibleSelectedCount = filteredSelectableAccounts.filter((a) =>
    selectedAccountIds.includes(a.id)
  ).length;
  const allChecked =
    visibleSelectableCount > 0 &&
    visibleSelectedCount === visibleSelectableCount;
  const isIndeterminate =
    visibleSelectedCount > 0 && visibleSelectedCount < visibleSelectableCount;

  const requiresNotice = !!selectedCountryId && !selectedTemplateId;

  const handleAdd = () => {
    if (
      selectedAccountIds.length === 0 ||
      (requiresNotice && !hasReadNotice) ||
      !organisationId
    ) {
      return;
    }
    dispatch(saveChartOfAccountsRequest(organisationId, selectedAccountIds));
    onSuccess?.();
  };

  return (
    <>
      <CustomDrawerBodyStyle />
      <CustomNoticeLinkStyle />
      <Wrapper
        className={[
          'add-chart-of-accounts-form-wrapper',
          pwaMobileNav ? 'scope-pwa-setup-picker-form' : undefined,
        ]
          .filter(Boolean)
          .join(' ')}
        pwaMobile={pwaMobileNav}
      >
        <div className="top-section">
          <LabelWrapper style={labelStyle}>
            <span className="label-text">{t('organisation.select-template')}</span>
          </LabelWrapper>
          <FormSelect
            placeholder={
              templates && Array.isArray(templates) && templates.length > 0
                ? t('organisation.select-template')
                : t('organisation.templates-not-available')
            }
            value={selectedTemplateId}
            onChange={(val) => {
              const nextId = val as string | undefined;
              setSelectedTemplateId(nextId);

              if (nextId) {
                // When a template is selected, clear country selection
                setSelectedCountryId(undefined);
              } else if (initialCountryId) {
                // When template is cleared, fall back to the default country
                setSelectedCountryId(initialCountryId);
              }

              setSelectedAccountIds([]);
            }}
            allowClear
            showSearch
            optionFilterProp="children"
            width="100%"
            height="45px"
            bordercolor="var(--color-border, #dbeae3)"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            style={{
              ...dropdownStyle,
              fontStyle:
                !templates ||
                !Array.isArray(templates) ||
                templates.length === 0
                  ? 'italic'
                  : 'normal',
            }}
            disabled={
              !templates || !Array.isArray(templates) || templates.length === 0
            }
          >
            {Array.isArray(templates) && templates.length > 0
              ? templates.map((tpl: any) => (
                  <FormSelect.Option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </FormSelect.Option>
                ))
              : null}
          </FormSelect>
          {/* Hidden country selector: kept for logic (default country & fetch) but not visible to user */}
          <div style={{ display: 'none' }}>
            <LabelWrapper style={labelStyle}>
              <span className="label-text">{t('organisation.select-country')}</span>
            </LabelWrapper>
            <FormSelect
              placeholder={
                filteredCountries.length === 0
                  ? t('organisation.countries-not-available')
                  : t('organisation.select-country')
              }
              value={selectedCountryId}
              onChange={(val) => {
                const nextCountryId = val as string | undefined;
                setSelectedCountryId(nextCountryId);
                setSelectedTemplateId(undefined);
                setSelectedAccountIds([]);
              }}
              allowClear
              showSearch
              optionFilterProp="children"
              width="100%"
              height="45px"
              bordercolor="var(--color-border, #dbeae3)"
              bgcolor="var(--color-surface-raised, #FFFFFF)"
              style={{
                ...countryDropdownStyle,
                fontStyle: filteredCountries.length === 0 ? 'italic' : 'normal',
              }}
              disabled={filteredCountries.length === 0}
            >
              {filteredCountries.map((country) => (
                <FormSelect.Option key={country.id} value={country.id}>
                  {localizeCountry(country.iso, country.name)}
                </FormSelect.Option>
              ))}
            </FormSelect>
          </div>

          <LabelWrapper style={labelStyle}>
            <span className="label-text">{t('organisation.select-accounts')}</span>
          </LabelWrapper>
          {accountsToShow.length > 0 && (
            <RoundInput
              placeholder={t('organisation.search-accounts')}
              prefix={<SearchOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />}
              value={accountSearchTerm}
              onChange={(e) => setAccountSearchTerm(e.target.value)}
              allowClear
              bg_color="var(--color-surface-raised, #ffffff)"
              width="100%"
              height="36px"
              style={{
                marginBottom: 8,
                fontSize: 12,
              }}
            />
          )}
          <AccountListWrapper
            deduct={selectedTemplateId ? 380 : selectedCountryId ? 380 : 380}
            pwaMobile={pwaMobileNav}
          >
            {accountsToShow.length > 0 && (
              <MasterCheckboxWrapper>
                <ScopeCheckbox
                  compact
                  aria-label={t('common.select-all')}
                  indeterminate={isIndeterminate}
                  checked={allChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const idsToAdd = filteredSelectableAccounts.map(
                        (a) => a.id
                      );
                      setSelectedAccountIds((prev) =>
                        Array.from(new Set([...prev, ...idsToAdd]))
                      );
                    } else {
                      const idsToRemove = new Set(
                        filteredSelectableAccounts.map((a) => a.id)
                      );
                      setSelectedAccountIds((prev) =>
                        prev.filter((id) => !idsToRemove.has(id))
                      );
                    }
                  }}
                  style={{ marginRight: 8 }}
                />
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: 11,
                    color: 'var(--color-placeholder, #9ca7b0)',
                    fontFamily: 'Inter, sans-serif',
                    fontStyle: 'italic',
                  }}
                >
                  {t('common.select-all')}
                </span>
              </MasterCheckboxWrapper>
            )}
            <div style={{ paddingTop: 12 }}>
              {accountsToShow.length === 0 ? (
                <div
                  style={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: 13,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    fontStyle: 'italic',
                  }}
                >
                  {t('organisation.no-accounts-available-select-above')}
                </div>
              ) : filteredAllAccounts.length === 0 ? (
                <div
                  style={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: 13,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    fontStyle: 'italic',
                  }}
                >
                  {t('organisation.no-accounts-match-search')}
                </div>
              ) : selectedTemplateId ? (
                <>
                  {filteredAllAccounts.map((account) => {
                    const existing =
                      existingCoaAccountIds.has(account.id) ||
                      existingCoaAccountKeys.has(
                        `${(account.name || '').trim()}::${account.type || ''}`
                      );
                    return (
                      <AccountItem
                        key={account.id}
                        style={existing ? { fontStyle: 'italic' } : undefined}
                      >
                        {existing ? (
                          <Tooltip title={t('organisation.already-in-your-chart-of-accounts')}>
                            <CheckOutlined
                              style={{
                                marginRight: 8,
                                color: 'var(--color-primary, #41aaaf)',
                                fontSize: 13,
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <ScopeCheckbox
                            compact
                            aria-label={account.name}
                            checked={selectedAccountIds.includes(account.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAccountIds([
                                  ...selectedAccountIds,
                                  account.id,
                                ]);
                              } else {
                                setSelectedAccountIds(
                                  selectedAccountIds.filter(
                                    (id) => id !== account.id
                                  )
                                );
                              }
                            }}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <AccountEllipsisWithTooltip
                          title={`${account.name} (${getExcelTransactionAccountTypeLabel(
                            account.type,
                            t
                          )})`}
                        >
                          {account.name || '-'}
                        </AccountEllipsisWithTooltip>
                        <AccountTypeLabel
                          title={getExcelTransactionAccountTypeLabel(account.type, t)}
                        >
                          {getExcelTransactionAccountTypeLabel(account.type, t)}
                        </AccountTypeLabel>
                      </AccountItem>
                    );
                  })}
                </>
              ) : (
                <>
                  {filteredRecommendedAccounts.length > 0 && (
                    <>
                      <SectionHeader>{t('organisation.recommended')}</SectionHeader>
                      {filteredRecommendedAccounts.map((account) => {
                        const existing =
                          existingCoaAccountIds.has(account.id) ||
                          existingCoaAccountKeys.has(
                            `${(account.name || '').trim()}::${
                              account.type || ''
                            }`
                          );
                        return (
                          <AccountItem
                            key={account.id}
                            style={
                              existing ? { fontStyle: 'italic' } : undefined
                            }
                          >
                            {existing ? (
                              <Tooltip title={t('organisation.already-in-your-chart-of-accounts')}>
                                <CheckOutlined
                                  style={{
                                    marginRight: 8,
                                    color: 'var(--color-primary, #41aaaf)',
                                    fontSize: 13,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <ScopeCheckbox
                                compact
                                aria-label={account.name}
                                checked={selectedAccountIds.includes(
                                  account.id
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAccountIds([
                                      ...selectedAccountIds,
                                      account.id,
                                    ]);
                                  } else {
                                    setSelectedAccountIds(
                                      selectedAccountIds.filter(
                                        (id) => id !== account.id
                                      )
                                    );
                                  }
                                }}
                                style={{ marginRight: 8 }}
                              />
                            )}
                            <AccountEllipsisWithTooltip
                              title={`${account.name} (${getExcelTransactionAccountTypeLabel(
                                account.type,
                                t
                              )})`}
                            >
                              {account.name || '-'}
                            </AccountEllipsisWithTooltip>
                            <AccountTypeLabel
                              title={getExcelTransactionAccountTypeLabel(account.type, t)}
                            >
                              {getExcelTransactionAccountTypeLabel(account.type, t)}
                            </AccountTypeLabel>
                          </AccountItem>
                        );
                      })}
                    </>
                  )}
                  {filteredOtherAccounts.length > 0 && (
                    <>
                      <SectionHeader>{t('organisation.other-accounts')}</SectionHeader>
                      {filteredOtherAccounts.map((account) => {
                        const existing =
                          existingCoaAccountIds.has(account.id) ||
                          existingCoaAccountKeys.has(
                            `${(account.name || '').trim()}::${
                              account.type || ''
                            }`
                          );
                        return (
                          <AccountItem
                            key={account.id}
                            style={
                              existing ? { fontStyle: 'italic' } : undefined
                            }
                          >
                            {existing ? (
                              <Tooltip title={t('organisation.already-in-your-chart-of-accounts')}>
                                <CheckOutlined
                                  style={{
                                    marginRight: 8,
                                    color: 'var(--color-primary, #41aaaf)',
                                    fontSize: 13,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <ScopeCheckbox
                                compact
                                aria-label={account.name}
                                checked={selectedAccountIds.includes(
                                  account.id
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedAccountIds([
                                      ...selectedAccountIds,
                                      account.id,
                                    ]);
                                  } else {
                                    setSelectedAccountIds(
                                      selectedAccountIds.filter(
                                        (id) => id !== account.id
                                      )
                                    );
                                  }
                                }}
                                style={{ marginRight: 8 }}
                              />
                            )}
                            <AccountEllipsisWithTooltip
                              title={`${account.name} (${getExcelTransactionAccountTypeLabel(
                                account.type,
                                t
                              )})`}
                            >
                              {account.name || '-'}
                            </AccountEllipsisWithTooltip>
                            <AccountTypeLabel
                              title={getExcelTransactionAccountTypeLabel(account.type, t)}
                            >
                              {getExcelTransactionAccountTypeLabel(account.type, t)}
                            </AccountTypeLabel>
                          </AccountItem>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </div>
          </AccountListWrapper>
        </div>
        <div className="bottom-section">
          {selectedCountryId && !selectedTemplateId && (
            <div
              style={{
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <ScopeCheckbox
                id="read-notice-coa-checkbox"
                checked={hasReadNotice}
                onChange={(e) => setHasReadNotice(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <label
                htmlFor="read-notice-coa-checkbox"
                style={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '13px',
                  color: 'var(--color-text-dark, #042a2a)',
                  fontFamily: 'Inter, sans-serif',
                  marginRight: 8,
                }}
              >
                {t('organisation.i-have-read-the-notice')}
              </label>
              <a
                href="#"
                className="custom-notice-link-coa"
                onClick={(e) => {
                  e.preventDefault();
                  setHasReadNotice(true);
                  dispatch(
                    turnMessageOn({
                      title: t('organisation.notice'),
                      description: (
                        <span>
                          We have compiled a default chart of accounts to help
                          get you started. However, please note the following:
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: 18,
                              listStyleType: 'disc',
                            }}
                          >
                            <li>
                              the accounts provided serve as a guideline and may
                              need to be adjusted for your entity
                            </li>
                            <li>
                              you can edit account names and types after adding
                              them from the Chart of Accounts tab
                            </li>
                          </ul>
                        </span>
                      ),
                      type: MessageStates.INFO,
                      duration: 15,
                    })
                  );
                }}
                tabIndex={0}
              >
                {t('organisation.open-notice')}
              </a>
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: onSkipCoa ? 'minmax(0, 1fr) auto' : '1fr',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <ScopeButton
              type="primary"
              width="100%"
              height="45px"
              fontSize="14px"
              style={{ minWidth: 0, padding: '0 20px' }}
              disabled={
                selectedAccountIds.length === 0 ||
                (requiresNotice && !hasReadNotice)
              }
              onClick={handleAdd}
            >
              {selectedAccountIds.length === 0
                ? t('organisation.add-accounts')
                : t('organisation.add-accounts-count', {
                    count: selectedAccountIds.length,
                  })}
            </ScopeButton>
            {onSkipCoa && (
              <ScopeButton
                type="default"
                width="auto"
                height="45px"
                fontSize="14px"
                style={{
                  minWidth: 0,
                  padding: '0 24px',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'transparent',
                  borderColor: 'transparent',
                  color: 'var(--color-primary, #41aaaf)',
                  fontWeight: 600,
                }}
                onClick={onSkipCoa}
              >
                {t('organisation.skip-for-now')}
              </ScopeButton>
            )}
          </div>
        </div>
      </Wrapper>
    </>
  );
};
